import { useState } from 'react';

import { useSkillsRepository } from '../../hooks/use-skills-repository';
import { ResizableListPane } from '../../shell/ResizableListPane';
import { fmtWhen, mono, primaryButton, secondaryButton } from '../proxy-vault/ui';

import { SkillRepositoryDetail } from './SkillRepositoryDetail';
import { SkillUploadModal, type UploadTarget } from './SkillUploadModal';

/** Console help — shipped verbatim from the plan (Appendix M.2). */
export const SKILLS_REPOSITORY_HELP =
  'Skills Repository — bundles uploaded by your integration or by you. Upload a zip to create a skill, ' +
  'publish new versions, roll back by setting an older version current, or delete a skill (its history is kept). ' +
  'Conversations bind a skill by selecting its id; a deleted skill is unbound on the next turn.';

/**
 * Skills Repository admin view: uploaded skill bundles (`/skills-repository`),
 * list → detail like the git-authored Skills view next to it. The list shows
 * the tenant's own skills and those inherited from ancestor tenants.
 */
export function SkillsRepositoryView() {
  const repo = useSkillsRepository();
  const [uploadTarget, setUploadTarget] = useState<UploadTarget | null>(null);

  const handleUpload = async (bundle: File, summary: string): Promise<void> => {
    if (!uploadTarget) return;
    if (uploadTarget.kind === 'create') {
      await repo.upload(bundle, summary);
    } else if (repo.selectedId) {
      await repo.uploadVersion(repo.selectedId, bundle, summary);
    }
    setUploadTarget(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: 'var(--bg)' }}>
      <div
        style={{
          padding: '12px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexShrink: 0,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Skills Repository</div>
          <div data-testid="skrepo-help" style={{ fontSize: 11, color: 'var(--text-soft)', lineHeight: 1.5, marginTop: 2 }}>
            {SKILLS_REPOSITORY_HELP}
          </div>
        </div>
        <button
          type="button"
          data-testid="skrepo-upload"
          style={primaryButton}
          onClick={() => setUploadTarget({ kind: 'create' })}
        >
          Upload skill
        </button>
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <ResizableListPane
          storageKey="skills-repository"
          defaultWidth={340}
          minWidth={240}
          maxWidth={560}
          paneStyle={{
            borderRight: '1px solid var(--border)',
            overflowY: 'auto',
            background: 'var(--bg-alt)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {repo.status === 'loading' && repo.skills.length === 0 && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)' }}>Loading skills…</div>
          )}
          {repo.status === 'error' && (
            <div data-testid="skrepo-list-error" style={{ padding: 16, fontSize: 12, color: 'var(--kind-failure)' }}>
              {repo.error ?? 'Failed to load skills.'}
            </div>
          )}
          {repo.status === 'success' && repo.skills.length === 0 && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)' }}>
              No skills yet. Upload a zip to create the first one.
            </div>
          )}
          {repo.skills.map((s) => {
            const isSelected = s.id === repo.selectedId;
            return (
              <button
                key={s.id}
                type="button"
                data-testid={`skrepo-row-${s.name}`}
                onClick={() => repo.setSelectedId(s.id)}
                aria-current={isSelected ? 'true' : undefined}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: 0,
                  borderBottom: '1px solid var(--border)',
                  background: isSelected ? 'var(--bg-alt2, var(--bg-alt))' : 'transparent',
                  color: 'var(--text)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.name}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>
                    {s.current_version ? `v${s.current_version.version}` : 'no version'}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-mute)', marginLeft: 'auto' }}>{fmtWhen(s.updated_at)}</span>
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--text-soft)',
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {s.description || 'No description.'}
                </span>
                <span style={{ fontSize: 10, color: 'var(--text-mute)', fontFamily: mono }}>
                  by {s.created_by ?? '—'}
                </span>
              </button>
            );
          })}
          {repo.hasMore && (
            <button
              type="button"
              data-testid="skrepo-load-more"
              style={{ ...secondaryButton, margin: 12 }}
              onClick={() => void repo.loadMore()}
            >
              Load more
            </button>
          )}
        </ResizableListPane>

        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {repo.selectedId && repo.detailStatus === 'loading' && !repo.detail && (
            <Centered>Loading skill…</Centered>
          )}
          {repo.selectedId && repo.detailStatus === 'error' && (
            <Centered error>{repo.detailError ?? 'Failed to load skill.'}</Centered>
          )}
          {repo.selectedId && repo.detail && repo.detailStatus !== 'error' && (
            <SkillRepositoryDetail
              skill={repo.detail}
              onNewVersion={() => setUploadTarget({ kind: 'version', skillName: repo.detail?.name ?? '' })}
              onSetCurrent={async (versionId) => {
                await repo.setCurrentVersion(repo.detail!.id, versionId);
              }}
              onDelete={() => repo.remove(repo.detail!.id)}
            />
          )}
          {!repo.selectedId && <Centered>Select a skill from the left, or upload one.</Centered>}
        </div>
      </div>

      {uploadTarget && (
        <SkillUploadModal target={uploadTarget} onCancel={() => setUploadTarget(null)} onUpload={handleUpload} />
      )}
    </div>
  );
}

function Centered({ children, error }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        color: error ? 'var(--kind-failure)' : 'var(--text-mute)',
        fontSize: 13,
        textAlign: 'center',
      }}
    >
      {children}
    </div>
  );
}
